import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Megaphone, HardHat, ShieldCheck, ArrowRight, ExternalLink, Clock, Landmark, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { fmtDate, daysLeft } from "@/lib/format";

const ICONS = { communication: Megaphone, batiment: HardHat, numerique: ShieldCheck };

function setPageSeo({ title, description, canonical }) {
  if (title) document.title = title;
  const setMeta = (key, content, attr = "name") => {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
    el.setAttribute("content", content);
  };
  setMeta("description", description);
  setMeta("og:title", title, "property");
  setMeta("og:description", description, "property");
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
  if (canonical) link.setAttribute("href", canonical);
}

export function SectorsIndex() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/public/sector-index").then(({ data }) => setData(data)).catch(() => {}); }, []);
  useEffect(() => { setPageSeo({ title: "Appels d'offres publics par secteur et département | Le Café des Marchés", description: "Explorez les marchés publics en cours par métier et par département : communication, bâtiment, numérique. Marchés réels issus du BOAMP, pour TPE et PME.", canonical: "https://lecafedesmarches.com/secteurs" }); }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="max-w-7xl mx-auto px-5 py-16">
        <span className="text-xs font-mono font-bold tracking-[0.2em] uppercase text-blue-700 bg-blue-50 px-2.5 py-1 rounded border border-blue-200">Marchés publics par secteur et département</span>
        <h1 className="mt-5 font-display text-4xl md:text-5xl font-extrabold text-slate-900 max-w-3xl leading-tight">Les appels d'offres près de chez vous, secteur par secteur</h1>
        <p className="mt-4 text-slate-600 max-w-2xl">Explorez les marchés publics en cours selon votre métier et votre territoire. Chaque page affiche des marchés réels, issus du BOAMP.</p>

        <div className="mt-12 space-y-10">
          {data?.sectors?.map((s) => {
            const Icon = ICONS[s.niche] || Megaphone;
            return (
              <div key={s.niche}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
                  <h2 className="font-display text-xl font-bold text-slate-900"><Link to={`/secteurs/${s.niche}`} className="hover:text-primary">{s.label}</Link></h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link to={`/secteurs/${s.niche}`} className="text-sm px-3 py-1.5 rounded-full border border-border bg-white hover:border-primary hover:text-primary transition-colors">Toute la France</Link>
                  {s.deps.map((d) => (
                    <Link key={d.code} to={`/secteurs/${s.niche}/${d.code}`} data-testid={`sector-link-${s.niche}-${d.code}`}
                      className="text-sm px-3 py-1.5 rounded-full border border-border bg-white hover:border-primary hover:text-primary transition-colors">{d.name} ({d.code})</Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export function SectorDetail() {
  const { niche, dep } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = dep ? `?niche=${niche}&dep=${dep}` : `?niche=${niche}`;
    api.get(`/public/sector${q}`).then(({ data }) => { setData(data); setLoading(false); }).catch(() => setLoading(false));
  }, [niche, dep]);

  useEffect(() => {
    if (!data) return;
    const loc = data.dep_name ? ` à ${data.dep_name} (${dep})` : " en France";
    const t = `Appels d'offres ${data.short}${loc} : ${data.count} marchés en cours | Le Café des Marchés`;
    const d = `${data.count} appels d'offres ${data.short}${data.dep_name ? ` à ${data.dep_name}` : ""} en cours, triés pour les TPE et PME. Recevez-les chaque matin par email. Source officielle BOAMP.`;
    setPageSeo({ title: t, description: d, canonical: `https://lecafedesmarches.com/secteurs/${niche}${dep ? `/${dep}` : ""}` });
  }, [data, niche, dep]);

  const Icon = ICONS[niche] || Megaphone;
  const title = data ? `Appels d'offres ${data.short}${data.dep_name ? ` ${data.dep_name}` : " en France"}` : "";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="max-w-5xl mx-auto px-5 py-14">
        <nav className="text-sm text-slate-400 mb-6">
          <Link to="/secteurs" className="hover:text-primary">Secteurs</Link> / <span className="text-slate-600">{data?.short}</span>{data?.dep_name && <> / <span className="text-slate-600">{data.dep_name}</span></>}
        </nav>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center"><Icon className="w-6 h-6 text-primary" /></div>
          <span className="text-xs font-mono font-bold uppercase text-blue-700">{data?.count ?? "…"} marchés en cours</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900">{title}</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">{data?.intro}</p>

        <div className="mt-6 rounded-2xl bg-blue-50 border border-blue-100 p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-700">Recevez chaque matin ces marchés, triés et expliqués, directement par email.</p>
          <Button onClick={user ? () => navigate("/dashboard") : login} className="rounded-full shrink-0" data-testid="sector-cta-btn">
            {user ? "Mon espace" : "Commencer, 0€ aujourd'hui"} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        <div className="mt-8 space-y-3">
          {loading && <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}
          {!loading && data?.tenders?.length === 0 && <p className="text-slate-400 py-10 text-center">Aucun marché en cours pour ce secteur, revenez bientôt.</p>}
          {!loading && data?.tenders?.map((t) => {
            const dl = daysLeft(t.date_limite);
            return (
              <a key={t.idweb} href={t.url} target="_blank" rel="noopener noreferrer"
                className="block rounded-2xl bg-white border border-border p-5 hover-lift" data-testid={`sector-tender-${t.idweb}`}>
                <div className="flex items-center justify-between mb-2">
                  {t.marche_adapte && <span className="text-[11px] font-mono font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">Marché adapté (TPE/PME)</span>}
                  {dl != null && dl >= 0 && <span className="text-xs font-mono text-amber-600 flex items-center gap-1 ml-auto"><Clock className="w-3 h-3" /> J-{dl}</span>}
                </div>
                <h3 className="font-semibold text-slate-900 leading-snug line-clamp-2">{t.objet}</h3>
                <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Landmark className="w-3.5 h-3.5" /> {t.acheteur}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {(t.departements || []).join(", ") || "France"}</span>
                  <span className="flex items-center gap-1">Limite : {fmtDate(t.date_limite)}</span>
                  <span className="flex items-center gap-1 text-primary ml-auto">Avis officiel <ExternalLink className="w-3.5 h-3.5" /></span>
                </div>
              </a>
            );
          })}
        </div>
        <p className="mt-8 text-xs text-slate-400">Source : BOAMP / DILA, Licence Ouverte Etalab 2.0. Les dossiers de consultation se téléchargent sur la plateforme de l'acheteur.</p>
      </section>
      <Footer />
    </div>
  );
}
